import { useEffect, useState } from 'react'

import { algoliaClient, clientsIndexName } from '@/shared/lib/algolia'
import { Client } from '@monorepo/functions/src/types/client'

interface UseAlgoliaClientsParams {
    searchQuery?: string
    classificationFilter?: string
    page?: number
    hitsPerPage?: number
}

interface UseAlgoliaClientsResponse {
    results: Client[]
    isLoading: boolean
    totalHits: number
    totalPages: number
    currentPage: number
}

export const useAlgoliaClients = ({
    searchQuery = '',
    classificationFilter,
    page = 0,
    hitsPerPage = 20,
}: UseAlgoliaClientsParams = {}): UseAlgoliaClientsResponse => {
    const [results, setResults] = useState<Client[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [totalHits, setTotalHits] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState(0)

    useEffect(() => {
        if (!algoliaClient) {
            console.warn('Algolia client is not configured')
            setResults([])
            setTotalHits(0)
            setTotalPages(0)
            setCurrentPage(0)
            setIsLoading(false)
            return
        }

        setIsLoading(true)

        const searchClients = async () => {
            if (!algoliaClient || !clientsIndexName) {
                setResults([])
                setTotalHits(0)
                setTotalPages(0)
                setCurrentPage(0)
                setIsLoading(false)
                return
            }

            try {
                const searchParams: Record<string, string | number> = {
                    hitsPerPage,
                    page,
                }

                if (classificationFilter && classificationFilter !== 'all') {
                    searchParams.filters = `classification:${classificationFilter}`
                }

                const requestParams: {
                    indexName: string
                    params: Record<string, string | number>
                    query?: string
                } = {
                    indexName: String(clientsIndexName),
                    params: searchParams,
                }

                if (searchQuery) {
                    requestParams.query = String(searchQuery)
                }

                const response = await algoliaClient.search([requestParams])

                const firstResult = response.results[0]
                if (!firstResult || !('hits' in firstResult)) {
                    setResults([])
                    setTotalHits(0)
                    setTotalPages(0)
                    setCurrentPage(0)
                    return
                }

                const clients = firstResult.hits.map((hit: { objectID: string }) => ({
                    ...hit,
                    id: hit.objectID,
                })) as Client[]

                setResults(clients)
                setTotalHits(firstResult.nbHits ?? 0)
                setTotalPages(firstResult.nbPages ?? 0)
                setCurrentPage(firstResult.page ?? 0)
            } catch (error) {
                console.error('Error searching clients in Algolia:', error)
                setResults([])
                setTotalHits(0)
                setTotalPages(0)
                setCurrentPage(0)
            } finally {
                setIsLoading(false)
            }
        }

        searchClients()
    }, [searchQuery, classificationFilter, page, hitsPerPage])

    return {
        results,
        isLoading,
        totalHits,
        totalPages,
        currentPage,
    }
}
